import store from "@/store/store";
import dayjs from "dayjs";

export function scrollToTop() {
  window.scrollTo({ top: 0, behavior: "smooth" });
}

export function amountFormatter(params) {
  let amount = params?.value || params;
  return parseFloat(amount) > 0
    ? "$" +
        Number(amount).toLocaleString("en-US", {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        })
    : "$0.00";
}

export function percentageFormatter(value) {
  return parseFloat(value) > 0
    ? parseFloat(value * 100).toFixed(2) + "%"
    : "0.00%";
}

export function addPercentage(params) {
  params = params?.value || params;
  return params + "%";
}

export const entityData = {
  header: "Entity You Represent *",
  data: [
    {
      id: "I",
      value: "Insurance",
      label: "Insurance Company",
    },
    {
      id: "P",
      value: "Product",
      label: "Product Manufacturer",
    },
    {
      id: "R",
      value: "Internal",
      label: "RICC Operator (Internal Use)",
    },
  ],
};

export function checkTwoFields(value1, value2, strictCheck) {
  if (strictCheck) {
    return value2 && value1 !== value2;
  } else {
    return value2 && value1 && value1.toLowerCase() !== value2.toLowerCase();
  }
}

export async function encryptData(data) {
  let publicKey = store.getState()?.commonReducer?.settings?.publicKey;

  const encrypted = await rsaEncrypt(data, publicKey);
  return encrypted;
}

async function importRsaPublicKey(pem) {
  const binaryDer = Uint8Array.from(atob(pem), (c) => c.charCodeAt(0));
  return await window.crypto.subtle.importKey(
    "spki",
    binaryDer.buffer,
    {
      name: "RSA-OAEP",
      hash: "SHA-256",
    },
    false,
    ["encrypt"]
  );
}

async function rsaEncrypt(data, publicKeyPem) {
  const key = await importRsaPublicKey(publicKeyPem);
  const encoder = new TextEncoder();
  const encoded = encoder.encode(data);

  const encrypted = await window.crypto.subtle.encrypt(
    {
      name: "RSA-OAEP",
    },
    key,
    encoded
  );
  return btoa(String.fromCharCode(...new Uint8Array(encrypted)));
}

export function getStatus(status) {
  const value = typeof status === "object" ? status?.value : status;
  switch (value) {
    case "P":
      return "Pending";
    case "E":
      return "Active";
    case "S":
      return "Suspended";
    case "R":
      return "Rejected";
    default:
      return "";
  }
}

export function getFormattedDateTime(params) {
  return dayjs(params.value).format("MM/DD/YYYY h:mm:ss A");
}

export function getFormattedDate(params, strict) {
  let date = params.value || params;
  if (!date || date.value === null) {
    return "";
  }
  if (date.length == 10 && !strict) {
    return date;
  }
  return dayjs(date).format("MM/DD/YYYY");
}

export function getCurrentDate() {
  return dayjs().format("MM/DD/YYYY");
}

export function checkGreaterThanCurrentDate(date) {
  if (date) return dayjs(date).isAfter(dayjs(), "day");
  else return false;
}

export function formatRoleCode(params) {
  let roleCode = params.value || params;
  switch (roleCode) {
    case "P":
      return "Primary";
    case "S":
      return "Secondary";
    case "T":
      return "Technical";
  }
}
export function formatOwnershipTypeCode(params) {
  let ownershipTypeCode = params.value || params;
  switch (ownershipTypeCode) {
    case "O":
      return "Original";
    case "B":
      return "Bene";
    case "Q":
      return "QDRO";
    case "N":
      return "Non Spousal Bene";
  }
}

export function formatGenderCode(params) {
  let gender = params.value || params;
  switch (gender) {
    case "M":
      return "Male";
    case "F":
      return "Female";
    case "U":
      return "Unknown";
  }
}

export function yesNoFormatter(params) {
  let value = params.value || params;
  switch (value) {
    case "Y":
      return "Yes";
    case "N":
      return "No";
    case "Z":
      return "Not Applicable";
    default:
      return params.value || "";
  }
}

export function formatPhoneNumber(params) {
  let phoneNumber = params.value || params;
  if (!phoneNumber) {
    return "";
  }
  phoneNumber = phoneNumber.replace(/\D/g, "");
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
    3,
    6
  )}-${phoneNumber.slice(6)}`;
}

export function sortByName(array) {
  return array.sort(function (a, b) {
    if (a.name < b.name) {
      return -1;
    }
    if (a.name > b.name) {
      return 1;
    }
    return 0;
  });
}

export function validateAge(ageStr, rowIndex, colIndex) {
  const trimmed = ageStr?.trim();
  if (!trimmed || trimmed.length === 0) {
    throw new Error(
      `Value is empty at (row:col) (${rowIndex + 1}:${colIndex + 1})`
    );
  }

  const parsedAge = parseInt(trimmed);
  if (isNaN(parsedAge) || parsedAge <= 0) {
    throw new Error(
      `Invalid value: '${ageStr}' at (row:col) (${rowIndex + 1}:${
        colIndex + 1
      })`
    );
  }
}

export function validatePrice(priceStr, rowIndex, colIndex) {
  const trimmed = priceStr?.trim();
  if (!trimmed || trimmed.length === 0) {
    throw new Error(
      `Value is empty at (row:col) (${rowIndex + 1}:${colIndex + 1})`
    );
  }

  const parsed = parseFloat(trimmed);
  if (isNaN(parsed) || parsed < 0) {
    throw new Error(
      `Invalid value: '${priceStr}' at (row:col) (${rowIndex + 1}:${
        colIndex + 1
      })`
    );
  }

  const [integerPart] = trimmed.split(".");
  if (integerPart.replace(/^0+/, "").length > 5) {
    throw new Error(
      `Too many digits before decimal in value: '${priceStr}' at (row:col) (${
        rowIndex + 1
      }:${colIndex + 1}). Max allowed is 5 digits.`
    );
  }
}

export function getProductOptionValue(location, type) {
  return (
    location?.state?.productOptionSettings?.find((e) => e.type === type)
      ?.value || ""
  );
}

export function formatDecimalField(params, minVal, maxVal, decimalPoint) {
  let newValue = String(params.value ?? "")?.replace(/[^0-9.]/g, "");
  const parts = newValue.split(".");
  if (parts.length > 2) {
    newValue = parts[0] + "." + parts.slice(1).join("");
  }
  let num = parseFloat(newValue);
  if (isNaN(num)) {
    return minVal;
  }
  num = Math.max(minVal, Math.min(maxVal, num));
  return parseFloat(num).toFixed(decimalPoint);
}

export function addOneDayToDate() {
  const a = new Date();
  a.setDate(a.getDate() + 1);
  return a;
}

export function formatNumberWithCommas(params, numberOfDecimalDigits) {
  const number = parseFloat(params.value);
  if (isNaN(number)) return "";

  return number.toLocaleString(undefined, {
    minimumFractionDigits: numberOfDecimalDigits,
    maximumFractionDigits: numberOfDecimalDigits,
  });
}

export function parseNumber(val) {
  return val == null || val === ""
    ? NaN
    : parseFloat(String(val).replace(/[^0-9.-]+/g, ""));
}
